import { CRITERIA, type CriterionKey } from "../../lib/duo-rubric";
import type { Totals } from "../../lib/exam/scoring-types";

/**
 * Per-onderdeel breakdown — a two-column grid (one column on mobile) of
 * criterion rows, each a label + n/max + a track bar. The weakest
 * criterion's track + value read in `--warn` (the "bijslijpen" band);
 * everything else uses `--accent`. Wrapped in a Vertrouwd panel card so
 * the report reads as hero → criteria card → per-beurt card.
 */
export function CriteriaList({
  totals,
  weakest,
  title,
}: {
  totals: Totals;
  weakest: CriterionKey | null;
  title: string;
}) {
  return (
    <section
      aria-label={title}
      className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--panel)] p-5 sm:p-6 shadow-[var(--shadow)] space-y-4"
    >
      <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
      <ul className="grid grid-cols-1 gap-x-7 gap-y-4 sm:grid-cols-2">
        {CRITERIA.map((c) => {
          const max = totals.perCriterionMax[c.key] || 1;
          const got = totals.perCriterion[c.key];
          const pct = Math.max(0, Math.min(1, got / max));
          const isWeakest = c.key === weakest;
          return (
            <li key={c.key} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-medium text-[var(--ink)]">{c.label}</span>
                <span className="font-mono-badge text-[var(--ink-muted)] tabular-nums">
                  {got}/{max}
                </span>
              </div>
              <span
                aria-hidden
                className="block h-2 overflow-hidden rounded-full bg-[var(--panel-soft)]"
              >
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${pct * 100}%`,
                    background: isWeakest ? "var(--warn)" : "var(--accent)",
                  }}
                />
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
